import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Upload } from 'lucide-react'
import { toast } from 'sonner'
import {
  apiError,
  isNotFound,
  useBulkEnrolMutation,
  useEnrolSiteMutation,
  usePartnerQuery,
  useRemoveSiteMutation,
  useSmbOwnersQuery,
} from '@/store/api'
import type { BulkEnrolResult, Partner, SmbOwner, SmbOwnerStatus, SmbSite } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Table, THead, Th, Tr, Td, Pagination, SkeletonRows } from '@/components/ui/data-table'
import { EmptyState, ErrorState } from '@/components/ui/empty-state'
import { Term } from '@/components/ui/term'
import { Tooltip } from '@/components/ui/tooltip'
import { NotFound } from '@/pages/NotFound'
import { enrolmentRows } from '@/lib/csv'
import { fmtDate, fmtMoney, fmtNum } from '@/lib/format'

const STATUS_BADGES: Record<SmbOwnerStatus, { variant: 'neutral' | 'active'; label: string }> = {
  free: { variant: 'neutral', label: 'Free' },
  paid: { variant: 'active', label: 'Paid' },
}
const BULK_MAX = 500

/** Mirrors enrolBlocker() server-side. */
function enrolBlockedReason(p: Partner | undefined): string | null {
  if (!p) return null
  if (p.archivedAt) return 'This brand is archived.'
  if (p.suspension) return 'This brand is suspended — new SMB sites cannot be enrolled.'
  if (!p.offers.smb) return 'The SMB offer is not enabled for this brand.'
  return null
}

export function SmbSites() {
  const { slug = '' } = useParams()
  const { data: partner } = usePartnerQuery(slug)
  const blocked = enrolBlockedReason(partner)
  const [status, setStatus] = useState('live')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [enrolOpen, setEnrolOpen] = useState(false)
  const [csvOpen, setCsvOpen] = useState(false)
  const { data, isLoading, isError, error, refetch } = useSmbOwnersQuery({
    slug,
    status: ['free', 'paid', 'agency', 'left'].includes(status) ? status : undefined,
    removed: status === 'removed' ? '1' : undefined,
    search: search || undefined,
    page,
  })

  if (isError && isNotFound(error)) return <NotFound />

  const actions = (
    <>
      <BlockedTooltip reason={blocked}>
        <Button variant="secondary" disabled={!!blocked} onClick={() => setCsvOpen(true)}>
          <Upload className="size-4" /> Upload CSV
        </Button>
      </BlockedTooltip>
      <BlockedTooltip reason={blocked}>
        <Button disabled={!!blocked} onClick={() => setEnrolOpen(true)}>
          Enrol site
        </Button>
      </BlockedTooltip>
    </>
  )
  const filtered = status !== 'live' || !!search

  return (
    <>
      <PageHeader
        title="SMB sites"
        description={
          <>
            Websites you host, grouped by owner. Each owner has one Atarim account shared by all their sites, and each site adds 200
            one-off free credits from Atarim. When the owner picks a <Term id="monthly-credit-budget">monthly credit budget</Term> or
            the Website Owner plan (2,000 credits a month), you bill them and Atarim charges you the{' '}
            <Term id="expansion-charge">Expansion Charge</Term>.
          </>
        }
        actions={actions}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          ariaLabel="Status filter"
          value={status}
          onValueChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
          options={[
            { value: 'live', label: 'Free and paid' },
            { value: 'free', label: 'Free' },
            { value: 'paid', label: 'Paid' },
            { value: 'agency', label: 'Client of your agencies' },
            { value: 'left', label: 'Moved to another agency' },
            { value: 'removed', label: 'All sites removed' },
          ]}
        />
        <Input
          placeholder="Search URL or owner email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="max-w-xs"
        />
      </div>

      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : !isLoading && data?.total === 0 && !filtered ? (
        <EmptyState
          title="No SMB sites yet"
          message="Enrol a site with just its URL and the owner's email, or upload a CSV of many."
          action={actions}
        />
      ) : (
        <>
          <Table>
            <THead>
              <Th>Owner and sites</Th>
              <Th>Status</Th>
              <Th>Since</Th>
              <Th right>Monthly budget</Th>
              <Th right>Credits this month</Th>
              <Th right>Charge this period</Th>
            </THead>
            {isLoading ? (
              <SkeletonRows cols={6} />
            ) : (
              <tbody>
                {data!.rows.length === 0 && (
                  <Tr>
                    <Td colSpan={6} className="text-center text-muted-foreground">
                      No owners match these filters.
                    </Td>
                  </Tr>
                )}
                {data!.rows.map((owner) => (
                  <OwnerRow key={owner.id} owner={owner} slug={slug} currency={data!.currency} />
                ))}
              </tbody>
            )}
          </Table>
          {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
        </>
      )}

      <EnrolDialog slug={slug} open={enrolOpen} onOpenChange={setEnrolOpen} />
      <CsvDialog slug={slug} open={csvOpen} onOpenChange={setCsvOpen} />
    </>
  )
}

function BlockedTooltip({ reason, children }: { reason: string | null; children: React.ReactNode }) {
  if (!reason) return <>{children}</>
  return (
    <Tooltip content={reason}>
      <span tabIndex={0}>{children}</span>
    </Tooltip>
  )
}

function OwnerRow({ owner, slug, currency }: { owner: SmbOwner; slug: string; currency: string }) {
  const badge = STATUS_BADGES[owner.status]
  const c = owner.credits
  return (
    <Tr>
      <Td className="max-w-[24rem] align-top">
        <div className="truncate font-medium">{owner.ownerEmail}</div>
        <ul className="mt-1.5 space-y-1">
          {owner.sites.map((site) => (
            <SiteLine key={site.id} site={site} slug={slug} owner={owner} />
          ))}
        </ul>
      </Td>
      <Td className="align-top">
        {owner.agency ? (
          <Tooltip content={`Since ${fmtDate(owner.agency.at)}. Their agency funds their credits, so you no longer bill them a budget.`}>
            <span>
              <Badge variant="expiring">
                {owner.agency.state === 'with_host_agency' ? `Client of ${owner.agency.licence?.label ?? 'your agency'}` : 'Moved to another agency'}
              </Badge>
            </span>
          </Tooltip>
        ) : (
          <Badge variant={badge.variant}>{badge.label}</Badge>
        )}
        {owner.planSince && !owner.agency && (
          <div className="mt-1 text-xs text-muted-foreground">Website Owner plan since {fmtDate(owner.planSince)}</div>
        )}
      </Td>
      <Td className="align-top">
        {fmtDate(owner.paidSince ?? owner.createdAt)}
        <div className="text-xs text-muted-foreground">{owner.paidSince ? 'paid since' : 'enrolled'}</div>
      </Td>
      <Td right className="align-top">
        {owner.monthlyCredits ? `${fmtNum(owner.monthlyCredits)} credits` : <span className="text-muted-foreground">—</span>}
      </Td>
      <Td right className="align-top">
        {c ? (
          <Tooltip
            content={`${fmtNum(c.allocated)} this month + ${fmtNum(c.carriedOver)} carried over · ${fmtNum(c.freeUsed)} of ${fmtNum(owner.freeCreditsGranted)} free credits used`}
          >
            <span className="whitespace-nowrap">
              {owner.status === 'free'
                ? `${fmtNum(c.freeUsed)} / ${fmtNum(owner.freeCreditsGranted)} free`
                : `${fmtNum(c.used)} / ${fmtNum(c.allocated + c.carriedOver)}`}
              {c.carriedOver > 0 && <span className="block text-xs text-muted-foreground">incl. {fmtNum(c.carriedOver)} carried over</span>}
            </span>
          </Tooltip>
        ) : (
          <span className="text-muted-foreground">{fmtNum(owner.freeCreditsGranted)} free</span>
        )}
      </Td>
      <Td right className="align-top">
        {owner.periodChargeMinor ? fmtMoney(owner.periodChargeMinor, currency) : <span className="text-muted-foreground">—</span>}
      </Td>
    </Tr>
  )
}

function SiteLine({ site, slug, owner }: { site: SmbSite; slug: string; owner: SmbOwner }) {
  const removed = site.status === 'removed'
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className={removed ? 'truncate text-muted-foreground line-through' : 'truncate'}>{site.siteUrl}</span>
      {!site.platformReady && !removed && (
        <Tooltip content={site.platformError ?? 'Atarim is still setting this site up. It retries automatically.'}>
          <span>
            <Badge variant="pending">Setting up</Badge>
          </span>
        </Tooltip>
      )}
      {removed ? (
        <span className="shrink-0 text-xs text-muted-foreground">removed {fmtDate(site.removedAt)}</span>
      ) : (
        <RemoveButton slug={slug} site={site} owner={owner} />
      )}
    </li>
  )
}

function RemoveButton({ slug, site, owner }: { slug: string; site: SmbSite; owner: SmbOwner }) {
  const [open, setOpen] = useState(false)
  const [remove, { isLoading }] = useRemoveSiteMutation()
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-ata-red hover:bg-ata-red/10" onClick={() => setOpen(true)}>
        Remove
      </Button>
      <AlertDialogContent title={`Remove ${site.siteUrl}?`}>
        <AlertDialogDescription>
          <p>
            All AI activity on this site, and its emails to {owner.ownerEmail}, stop <strong>immediately</strong>. The owner's account,
            data and other sites are untouched.
            {owner.status === 'paid' &&
              ' Their monthly budget keeps running until it lapses or is cancelled.'}{' '}
            To bring the site back, enrol it again.
          </p>
        </AlertDialogDescription>
        <div className="mt-5 flex justify-end gap-2">
          <AlertDialogCancel asChild>
            <Button variant="secondary">Keep site</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              loading={isLoading}
              onClick={async (e) => {
                e.preventDefault()
                try {
                  await remove({ slug, id: site.id }).unwrap()
                  toast.success('Site removed')
                  setOpen(false)
                } catch (err) {
                  toast.error(apiError(err))
                }
              }}
            >
              Remove site
            </Button>
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function EnrolDialog({ slug, open, onOpenChange }: { slug: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [enrol, { isLoading }] = useEnrolSiteMutation()
  const [siteUrl, setSiteUrl] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const { site, owner } = await enrol({ slug, siteUrl, ownerEmail }).unwrap()
      toast.success(owner.sites.length > 1 ? `${site.siteUrl} added to ${owner.ownerEmail}'s account` : `${site.siteUrl} enrolled`)
      setSiteUrl('')
      setOwnerEmail('')
      onOpenChange(false)
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Enrol site"
        description="Atarim learns the business and proposes a plan to the owner. If this owner already has sites with you, this one joins their account and adds 200 free credits."
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="smb-url">Site URL</Label>
            <Input id="smb-url" required placeholder="https://example.com" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="smb-email">Owner email</Label>
            <Input id="smb-email" type="email" required placeholder="owner@example.com" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
          </div>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading}>
            Enrol site
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function CsvDialog({ slug, open, onOpenChange }: { slug: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [bulkEnrol, { isLoading }] = useBulkEnrolMutation()
  const fileInput = useRef<HTMLInputElement>(null)
  const [results, setResults] = useState<BulkEnrolResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const upload = async (file: File) => {
    setError(null)
    setResults(null)
    const rows = enrolmentRows(await file.text())
    if (!rows.length) return setError('That file has no rows.')
    if (rows.length > BULK_MAX) return setError(`Upload at most ${BULK_MAX} sites at a time — this file has ${rows.length}.`)
    try {
      const res = await bulkEnrol({ slug, rows }).unwrap()
      setResults(res.results)
    } catch (err) {
      setError(apiError(err))
    }
  }

  const enrolled = results?.filter((r) => r.ok).length ?? 0
  const failed = results?.filter((r) => !r.ok) ?? []

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setResults(null)
        onOpenChange(o)
      }}
    >
      <DialogContent
        title="Upload sites"
        description="A CSV with a site URL and owner email per row — headed site_url, owner_email, or just those two columns."
        className="max-w-lg"
      >
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void upload(file)
            e.target.value = ''
          }}
        />
        <Button className="w-full" variant="secondary" loading={isLoading} onClick={() => fileInput.current?.click()}>
          <Upload className="size-4" /> Choose CSV file
        </Button>
        <FieldError>{error}</FieldError>
        {results && (
          <div className="mt-4 space-y-2 text-sm">
            <p>
              <strong>{fmtNum(enrolled)}</strong> enrolled
              {failed.length > 0 && (
                <>
                  , <strong>{fmtNum(failed.length)}</strong> not enrolled:
                </>
              )}
            </p>
            {failed.length > 0 && (
              <ul className="max-h-60 space-y-1 overflow-y-auto rounded-lg border border-border p-3 text-xs">
                {failed.map((r) => (
                  <li key={r.line}>
                    <span className="font-mono">Line {r.line}</span> {r.siteUrl && <span className="text-muted-foreground">{r.siteUrl}</span>} —{' '}
                    {r.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
